"use client";

import React from "react";
import { createPortal } from "react-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import { useGoogleLogin } from "@react-oauth/google";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./msalConfig"; // Ensure you have this config file set up
import bbImage from "../../assests/images/bbb.png";
import FeedbackModal from "../shared/modals/FeedbackModal";
import InputField from "../shared/InputValidation";
import MessageManager from "../shared/Message";
import { InteractionStatus } from "@azure/msal-browser";
import appConstants from "../shared/config/constants.json";

import {
  Eye,
  EyeOff,
  Mail,
  Lock,
  User,
  Building,
  Sparkles,
  Briefcase,
  TrendingUp,
  Loader2,
  X,
} from "lucide-react";
import "./signUp.css";
// import "./SocialCompletionModal.css";
// import Logo from "../layout/Logo";

import {
  loginApi,
  signUp,
  getGoogleUserProfile,
  getMicrosoftUserProfile,
  commonLoginApi,
  forgotPasswordApi,
} from "../../services/authService";
import { handleApiError } from "../shared/utils";
import {
  getRememberedCredentials,
  getRememberMePreference,
  persistAuthSession,
  persistRememberedCredentials,
  setPendingRememberMePreference,
} from "./authStorage";
import { useAuth } from "./AuthContext";
import { useNavigate } from "react-router-dom";

export default function SignIn() {
  const navigate = useNavigate();
  const { instance } = useMsal();
  const { login } = useAuth();
  const { signup } = useAuth();
  // inside SignIn()
  const { inProgress } = useMsal();
  const msLoginStartedRef = useRef(false);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  const { MAYAMAYA_DEV_SUPPORT_URL } = appConstants;

  const REDIRECT_URI = `${window.location.origin}/auth/microsoft/callback`;
  const POPUP_REDIRECT_URI = `${window.location.origin}/msal-popup.html`;

  const [isSignUpMode, setIsSignUpMode] = useState(false);
  const [isLoggedin, setIsLoggedin] = useState(false);
  const [rememberMe, setRememberMe] = useState(getRememberMePreference);
  const [formData, setFormData] = useState(() => {
    const rememberedCredentials = getRememberedCredentials();

    return {
      name: "",
      email: rememberedCredentials.email,
      organization: "",
      password: rememberedCredentials.password,
      website: "",
      checked: false,
    };
  });
  const [showModal, setShowModal] = useState({
    show: false,
    type: "",
    title: "",
    message: "",
  });

  const [errors, setErrors] = useState({
    email: "",
    password: "",
    name: "",
    organization: "",
    website: "",
  });

  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnimated, setIsAnimated] = useState(false);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // Add this temporary state for testing
  const [showCompletionModal, setShowCompletionModal] = useState(false); // Set to true for testing
  const [socialUserData, setSocialUserData] = useState({
    name: "John Doe",
    email: "john@example.com",
  });
  // ************************************************************************************************
  // --- Auth Type Mismatch Modal State ---
  const [authMismatchModal, setAuthMismatchModal] = useState({
    show: false,
    title: "",
    message: "",
  });

  // Helper function to handle 409 auth type mismatch errors
  const handleAuthTypeMismatch = (error) => {
    // Error can be either axios error or response data directly
    const errorMessage = error?.response?.data?.message || error?.message || "";
    const errorCode = error?.response?.data?.code || error?.code;

    if (errorCode === 409) {
      let authType = "email and password";
      let message = "";

      // Detect which auth type the account is set up with
      if (errorMessage.includes("INTERNAL")) {
        authType = "email and password";
      } else if (errorMessage.includes("GOOGLE")) {
        authType = "Google";
      } else if (errorMessage.includes("MICROSOFT")) {
        authType = "Microsoft";
      }

      message = `This account is set up with ${authType}.\nPlease use that option to sign in.`;

      setAuthMismatchModal({
        show: true,
        title: "",
        message: message,
      });
      return true;
    }
    return false;
  };

  // ************************************************************************************************
  // --- Forgot password modal state ---
  const [isForgotOpen, setIsForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSending, setForgotSending] = useState(false);
  const [forgotError, setForgotError] = useState("");

  const openForgot = useCallback((prefill = "") => {
    setForgotEmail(prefill || "");
    setForgotError("");
    setIsForgotOpen(true);
  }, []);

  const closeForgot = useCallback(() => {
    setIsForgotOpen(false);
    setForgotSending(false);
    setForgotError("");
  }, []);

  // ESC to close
  useEffect(() => {
    if (!isForgotOpen) return;
    const onKeyDown = (e) => e.key === "Escape" && closeForgot();
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isForgotOpen, closeForgot]);

  // (Optional) simple email validator
  const isValidEmail = (email) => /\S+@\S+\.\S+/.test(email);

  // TODO: replace the simulated API call with your real one, e.g. authService.requestPasswordReset
  const submitForgot = async (e) => {
    e.preventDefault();
    if (!isValidEmail(forgotEmail)) {
      setForgotError("Enter a valid email address.");
      return;
    }
    try {
      setForgotSending(true);

      const response = await forgotPasswordApi({ email: forgotEmail });

      if (response.code === 200 || response.status === 200) {
        closeForgot(); // Close modal

        MessageManager.showToast("success", `Password reset link sent`);
      }
      // await requestPasswordReset(forgotEmail); // <- your real API here
      // await new Promise((r) => setTimeout(r, 900)); // simulate
      // setShowModal({
      //   show: true,
      //   type: "success",
      //   title: "Reset link sent",
      //   message: `We’ve emailed a reset link to ${forgotEmail}.`,
      // });
      // if (response.code === 200) {
      // }
    } catch (err) {
      setForgotError("Something went wrong. Please try again.");
    } finally {
      closeForgot();
      setForgotSending(false);
    }
  };

  //  Show toast if redirected with reason=session_expired
  useEffect(() => {
    const url = new URL(window.location.href);
    const reason = url.searchParams.get("reason");

    if (reason === "session_expired") {
      MessageManager.showToast(
        "warning",
        "Session Expired: Please sign in again",
      );

      // ✅ remove reason so refresh doesn't show toast again
      url.searchParams.delete("reason");
      window.history.replaceState({}, "", url.toString());
    }
  }, []);

  useEffect(() => {
    setIsAnimated(true);
    const handleMouseMove = (e) => {
      setMousePos({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener("mousemove", handleMouseMove);
    return () => window.removeEventListener("mousemove", handleMouseMove);
  }, []);

  useEffect(() => {
    const token = sessionStorage.getItem("JWT");
    const email = sessionStorage.getItem("email");

    if (token && email) {
      navigate("/portal-selector", { replace: true });
    }
  }, [navigate]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });

    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: "",
      });
    }
  };

  const handleCheckboxChange = (e) => {
    setFormData({
      ...formData,
      checked: e.target.checked,
    });
  };

  const handleRememberMeChange = (e) => {
    const nextRememberMe = e.target.checked;
    setRememberMe(nextRememberMe);

    if (!nextRememberMe) {
      persistRememberedCredentials({}, false);
    }
  };

  const handleSocialSignupComplete = useCallback(async (completionData) => {
    setIsLoading(true);
    try {
      console.log("Completion data:", completionData);

      // Here you would normally call your signup API
      // For testing, just simulate success
      setTimeout(() => {
        setShowCompletionModal(false);
        setIsLoading(false);
        console.log("Modal would close and redirect to dashboard");
        // navigate("/home")
      }, 2000);
    } catch (error) {
      console.error("Signup completion error:", error);
      setIsLoading(false);
    }
  }, []);

  const handleCloseModal = useCallback(() => {
    setShowCompletionModal(false);
  }, []);
  const handleForgotPasword = (e) => {
    e.preventDefault();
    openForgot(formData.email);
  };

  // API CALLS
  // const handleLogin = async (e) => {
  //   e.preventDefault();
  //   // Prevent multiple submissions
  //   if (formData.email === "" || formData.password === "") {
  //     setShowModal({
  //       show: true,
  //       type: "error",
  //       title: "Missing Fields",
  //       message: "Please fill in all fields.",
  //     });
  //     return;
  //   }
  //   setIsLoading(true);
  //   try {
  //
  //     const payload = {
  //
  //       authType: "INTERNAL",
  //     };
  //     console.log("Submitting Login:", payload);
  //     // Make API request
  //     const response = await loginApi(payload);

  //     login(response); // Use the login function from AuthContext
  //     // sessionStorage.setItem("isFirstTimeUser", response.data.isSetupCompleted);

  //     navigate("/home");
  //   } catch (error) {
  //     console.error("Login error:", error);
  //     // handleApiError(error, { showModal: true });
  //   } finally {
  //     setIsLoading(false);
  //   }
  // };
  const [orgCode, setOrgCode] = useState("");
  const [organization, setOrganization] = useState("");
  const [department, setDepartment] = useState("");

  useEffect(() => {
    setOrgCode(sessionStorage.getItem("orgCode"));
    setOrganization(sessionStorage.getItem("organization"));
    setDepartment(sessionStorage.getItem("department"));

    // add the orgCode, organization, department from base url params if they are present in the sessionStorage
  }, []);
  // useEffect(() => {
  //   const orgCode = sessionStorage.getItem("orgCode");
  //   const organization = sessionStorage.getItem("organization");
  //   const department = sessionStorage.getItem("department");

  //   setOrgCode(orgCode || "");
  //   setOrganization(organization || "");
  //   setDepartment(department || "");

  //   // Add/overwrite query params without a page reload
  //   const url = new URL(window.location.href);
  //   if (organization) url.searchParams.set("organization", organization);
  //   if (department) url.searchParams.set("department", department);
  //   if (orgCode) url.searchParams.set("orgCode", orgCode);

  //   const next = url.toString();
  //   if (next !== window.location.href) {
  //     window.history.replaceState({}, "", next);
  //   }
  // }, []);

  const handleLogin = async (e) => {
    // sessionStorage.clear();
    e.preventDefault();
    console.log("Login clicked", formData);

    // Clear all errors before API call
    setErrors({
      email: "",
      password: "",
      name: "",
      organization: "",
      website: "",
    });
    setIsLoading(true);

    try {
      let obj = {};
      if (orgCode && organization && department) {
        obj = {
          email: formData.email,
          password: formData.password,
          authType: "INTERNAL",
          orgCode: orgCode,
          organization: organization,
          department: department,
        };
      } else {
        obj = {
          email: formData.email,
          password: formData.password,
          authType: "INTERNAL",
        };
      }
      const response = await commonLoginApi(obj);
      if (response.code == 200) {
        persistRememberedCredentials(
          {
            email: formData.email,
            password: formData.password,
          },
          rememberMe,
        );
        persistAuthSession(
          {
            JWT: response?.data?.token,
            email: response?.data?.email,
            accessLevel: response?.data?.access,
            orgCode,
            organization,
            department,
            tenantId: response?.data?.tenantId,
            mainTenantId: response?.data?.tenantId,
            userName: response?.data?.userName,
            isFirstTimeUser: response?.data?.isSetupCompleted,
          },
          rememberMe,
        );
        navigate("/portal-selector");
      }

      console.log("Login response:", response);
    } catch (error) {
      // handleApiError(error, { showModal: true });
      console.error("Login error:", error);

      // Check for auth type mismatch (409 error)
      if (handleAuthTypeMismatch(error)) {
        return;
      }

      // Extract error message from API response
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "An error occurred during login";

      const errorCode = error?.response?.data?.code || error?.code;

      const lowerError = errorMessage.toLowerCase();

      if (lowerError.includes("incorrect email or password")) {
        setErrors({
          email: " ",
          password: "Incorrect email or password",
          name: "",
          organization: "",
          website: "",
        });
      } else if (lowerError.includes("invalid encrypted userid or tenantid")) {
        setErrors({
          email: "Enter a valid email address",
          password: "",
          name: "",
          organization: "",
          website: "",
        });
      } else {
        //errors that don't map to specific fields, show in modal
        handleApiError(error, { showModal: true });
      }
    } finally {
      setIsLoading(false);
    }
  };
  const handleSignUp = async (e) => {
    e.preventDefault();
    console.log("Sign Up clicked", formData);

    setErrors({
      email: "",
      password: "",
      name: "",
      organization: "",
      website: "",
    });

    // Set loading state
    setIsLoading(true);
    try {
      // sessionStorage.clear();
      const payload = {
        adminEmail: formData.email,
        adminName: formData.name,
        adminOrg: formData.organization,
        authType: "INTERNAL",
        website: formData.website,
      };

      const response = await signUp(payload);

      signup(response); // Use the signUp function from AuthContext
      console.log("Sign Up response:", response);

      // Mark as first time user for onboarding
      // sessionStorage.setItem("isFirstTimeUser", response.data.isSetupCompleted);
      navigate("/home");
    } catch (error) {
      console.error("Sign Up error:", error);
      // handleApiError(error, setShowModal, navigate);

      // Check for auth type mismatch (409 error)
      if (handleAuthTypeMismatch(error)) {
        return;
      }

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "An error occurred during sign up";

      const lowerError = errorMessage.toLowerCase();

      if (
        lowerError.includes("invalid encrypted userid or tenantid") ||
        lowerError.includes("invalid email")
      ) {
        setErrors({
          email: "Enter a valid email address",
          password: "",
          name: "",
          organization: "",
          website: "",
        });
      } else {
        // Generic errors show in modal
        handleApiError(error, { showModal: true });
      }
    } finally {
      setIsLoading(false);
    }
  };
  const [socialSignupData, setSocialSignupData] = useState({});
  const isSafari =
    typeof navigator !== "undefined" &&
    /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Use one stable callback path for every environment
  const redirectUri = `${window.location.origin}/auth/google/callback`;

  const loginWithGoogle = useGoogleLogin({
    flow: "auth-code",
    // Safari may block popups; let it fall back to redirect.
    // Popup remains for Chrome/Edge/Firefox.
    ux_mode: "popup",
    // IMPORTANT: must be whitelisted in Google Console (Authorized redirect URIs)
    redirect_uri: redirectUri,
    // Optional: show account chooser each time
    prompt: "select_account",

    onSuccess: async (tokenResponse) => {
      try {
        console.log("Google user info:", tokenResponse);

        let obj = {};
        if (orgCode && organization && department) {
          obj = {
            // password: formData.password,
            orgCode: orgCode,
            organization: organization,
            department: department,

            // email: userInfo.data.email,
            authType: "GOOGLE",
            // id: ID,
            token: tokenResponse.code,
          };
        } else {
          obj = {
            // email: userInfo.data.email,
            authType: "GOOGLE",
            // id: ID,
            token: tokenResponse.code,
          };
        }

        // const loginUserDetails = await loginApi(obj);
        const loginUserDetails = await commonLoginApi(obj);
        console.log("Login User Details:", loginUserDetails);

        if (loginUserDetails.code == 200) {
          persistAuthSession(
            {
              JWT: loginUserDetails?.data?.token,
              email: loginUserDetails?.data?.email,
              accessLevel: loginUserDetails?.data?.access,
              orgCode,
              organization,
              department,
              tenantId: loginUserDetails?.data?.tenantId,
              mainTenantId: loginUserDetails?.data?.tenantId,
              userName: loginUserDetails?.data?.userName,
              isFirstTimeUser: loginUserDetails?.data?.isSetupCompleted,
            },
            rememberMe,
          );
          setSocialSignupData({
            email: loginUserDetails.data.email,
            name: loginUserDetails.data.name,
          });
          navigate("/portal-selector");
        }
      } catch (error) {
        console.error("Google login error:", error);

        // Check for auth type mismatch (409 error)
        if (handleAuthTypeMismatch(error)) {
          return;
        }

        handleApiError(error, { showModal: true });

        // New user → open completion modal
        if (error.code === 400 && error.message === "User not found") {
          setSocialUserData({
            email: socialSignupData.email,
            name: socialSignupData.name,
          });
          setShowCompletionModal(true);
          return;
        }
      }
    },

    onError: (err) => {
      console.error("Google login failed:", err);
    },
  });

  // Put this inside SignIn() (before handleMsAlLogin)
  const ua = navigator.userAgent || "";
  const isTouch =
    navigator.maxTouchPoints > 1 ||
    window.matchMedia?.("(pointer: coarse)")?.matches;

  const isMobileLike = /Android|iPhone|iPad|iPod/i.test(ua) || !!isTouch;

  // Optional: iPadOS "desktop mode" shows as Macintosh but still touch
  const isIPadDesktopMode = /Macintosh/i.test(ua) && !!isTouch;
  const handleMsAlLogin = async () => {
    // ✅ prevent parallel MSAL interactions (fixes interaction_in_progress)
    if (inProgress !== InteractionStatus.None) return;

    // ✅ prevent double clicks
    if (msLoginStartedRef.current) return;
    msLoginStartedRef.current = true;
    setPendingRememberMePreference(rememberMe);

    setIsLoading(true);

    try {
      // base request (scopes etc)
      // const request = { ...loginRequest };
      const request = { ...loginRequest, prompt: "select_account" };

      // ✅ Use redirect on mobile-like environments (including "desktop site" on phone)
      if (isMobileLike) {
        await instance.loginRedirect({
          ...request,
          redirectUri: REDIRECT_URI,
        });
        return; // page will redirect away
      }

      // ✅ Use popup on desktop + blank redirect page
      const response = await instance.loginPopup({
        ...request,
        redirectUri: POPUP_REDIRECT_URI,
      });

      // ✅ Set active account for later silent calls
      if (response?.account) {
        instance.setActiveAccount(response.account);
      }

      // ✅ Access token + profile
      const accessToken = response.accessToken;
      const msProfile = await getMicrosoftUserProfile(accessToken);

      const email =
        (msProfile?.mail || "").trim() ||
        (msProfile?.userPrincipalName || "").trim() ||
        (response?.account?.username || "").trim();
      const MSLoginId = msProfile?.id;
      console.log("Microsoft profile:", MSLoginId);
      if (!email) {
        throw new Error("Microsoft email not found");
      }

      // ✅ build backend login payload
      const orgCodeLS = sessionStorage.getItem("orgCode");
      const organizationLS = sessionStorage.getItem("organization");
      const departmentLS = sessionStorage.getItem("department");
      // console.log(MSLOGINID, "MSLOGINID");
      const payload =
        orgCodeLS && organizationLS && departmentLS
          ? {
              authType: "MICROSOFT",
              orgCode: orgCodeLS,
              organization: organizationLS,
              department: departmentLS,
              id: MSLoginId,
              token: response.idToken,
            }
          : { authType: "MICROSOFT", id: MSLoginId, token: response.idToken };

      const loginUserDetails = await commonLoginApi(payload);

      if (loginUserDetails?.code === 200) {
        persistAuthSession(
          {
            JWT: loginUserDetails?.data?.token,
            email: loginUserDetails?.data?.email || email,
            accessLevel: loginUserDetails?.data?.access,
            orgCode: orgCodeLS,
            organization: organizationLS,
            department: departmentLS,
            tenantId: loginUserDetails?.data?.tenantId,
            mainTenantId: loginUserDetails?.data?.tenantId,
            userName: loginUserDetails?.data?.userName,
            isFirstTimeUser: loginUserDetails?.data?.isSetupCompleted,
          },
          rememberMe,
        );

        navigate("/portal-selector");
      } else {
        navigate("/signin", { replace: true });
      }
    } catch (error) {
      console.error("MSAL Login error:", error);

      // Check for auth type mismatch (409 error)
      if (handleAuthTypeMismatch(error)) {
        setIsLoading(false);
        msLoginStartedRef.current = false;
        return;
      }

      handleApiError(error, { showModal: true });
    } finally {
      setIsLoading(false);
      msLoginStartedRef.current = false;
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSignUpMode) {
      handleSignUp(e);
    } else {
      handleLogin(e);
    }
  };

  const handleHelpSupportClick = () => {
    window.open(MAYAMAYA_DEV_SUPPORT_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="auth-main-container">
      {/* Subtle Background Effects */}
      <div
        className="auth-bg-dynamic"
        style={{
          background: `radial-gradient(300px circle at ${mousePos.x}px ${mousePos.y}px, rgba(14, 24, 34, 0.08), transparent 40%)`,
        }}
      ></div>

      <div className="auth-bg-elements">
        <div className="auth-floating-orb auth-orb-1"></div>
        <div className="auth-floating-orb auth-orb-2"></div>
      </div>

      <div className="auth-content-wrapper">
        {/* Left Side - Prominent Image & Content */}
        <div className="auth-showcase-section">
          {/* Prominent Image Display */}
          <div className="auth-image-display">
            <div className="auth-image-frame">
              <img
                src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/auth-HmXZj5A6u97g3wKvYIeYQvoN9EAw5m.png"
                alt="Talent Console Platform"
                className="auth-main-image"
              />
              <div className="auth-image-glow"></div>
            </div>
          </div>

          {/* Content Below Image */}
          <div className="auth-content-section">
            <div className="auth-brand-header">
              <div className="auth-logo-container">
                {/* <Logo width={70} height={35} className="auth-logo" /> */}
              </div>
              <h2 className="auth-brand-title">Talent Console</h2>
              <p className="auth-brand-description">
                AI-Powered Talent Management Platform
              </p>
            </div>

            <div className="auth-features-list">
              <div className="auth-feature">
                <div className="auth-feature-icon">
                  <Sparkles size={18} />
                </div>
                <div className="auth-feature-content">
                  <h4>AI-Powered Insights</h4>
                  <p>Advanced analytics for talent decisions</p>
                </div>
              </div>
              <div className="auth-feature">
                <div className="auth-feature-icon">
                  <Briefcase size={18} />
                </div>
                <div className="auth-feature-content">
                  <h4>Workforce Planning</h4>
                  <p>Identify future roles & skills gaps</p>
                </div>
              </div>
              <div className="auth-feature">
                <div className="auth-feature-icon">
                  <TrendingUp size={18} />
                </div>
                <div className="auth-feature-content">
                  <h4>Growth Analytics</h4>
                  <p>Track performance & development</p>
                </div>
              </div>
              <div className="securedContainer">
                <button
                  type="button"
                  className="helpSupportBtn"
                  onClick={handleHelpSupportClick}
                >
                  Help & Support
                </button>

                <div className="securedByWrap">
                  <p>Secured by </p>
                  <img src={bbImage} alt="BBB" />
                  <img
                    src="https://piiguard.com/assets/img/pii-guard-logo-white.svg"
                    alt="PII Guard"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Side - Compact Form */}
        <div className="auth-form-section">
          <div
            className={`auth-form-container ${
              isAnimated ? "auth-animate-in" : ""
            }`}
          >
            <div
              className="logo-container"
              style={{ display: "flex", justifyContent: "center" }}
            >
              <svg
                width="100"
                height="50"
                viewBox="0 0 120 60"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="career-auth-logo"
              >
                <path
                  d="M37.0024 0.946045L36.9688 1.02501V1.03354L37.0024 0.946045Z"
                  fill="currentColor"
                />
                <path
                  d="M52.4023 23.4972L52.4163 23.5281L52.4302 23.4929L52.4023 23.4972Z"
                  fill="currentColor"
                />
                <mask
                  id="mask0_3147_16711"
                  maskUnits="userSpaceOnUse"
                  x="10"
                  y="2"
                  width="100"
                  height="58"
                >
                  <path d="M110 2.82495H10V59.054H110V2.82495Z" fill="white" />
                </mask>
                <g mask="url(#mask0_3147_16711)">
                  <path
                    d="M99.824 7.49986L94.348 23.3389L102.302 45.1929H97.339L92.088 28.8199L86.365 45.1909H81.957C85.804 34.6299 93.532 13.3949 97.379 2.82886H102.306L109.999 23.9599L105.04 23.9709L99.824 7.49986Z"
                    fill="currentColor"
                  />
                  <path
                    d="M89.6626 24.0261L89.6516 24.0581L89.6406 24.0311L89.6626 24.0261Z"
                    fill="currentColor"
                  />
                  <path
                    d="M43.281 7.49195L48.471 23.937L53.43 23.929L45.754 2.82495H40.829C40.788 2.94395 35.638 17.125 35.6 17.241L30.362 2.82495H25.378C21.547 13.391 13.832 34.625 10 45.191H14.408L20.135 28.725L25.36 45.195H29.86L30.091 44.563L35.557 28.817L40.787 45.195H45.749L37.824 23.378C37.838 23.343 43.268 7.52395 43.281 7.49195ZM27.881 38.492C27.873 38.465 22.391 23.409 22.381 23.372L27.844 7.47195L33.109 24.108L27.881 38.492Z"
                    fill="currentColor"
                  />
                  <path
                    d="M63.8158 7.48186L58.3528 23.3249L66.3118 45.1889L61.3528 45.1939L56.1088 28.8249L50.3748 45.1679H45.9648C49.8188 34.5929 57.5358 13.3989 61.3888 2.82886H66.3158L73.9928 23.9289L69.0338 23.9369L63.8158 7.48186Z"
                    fill="currentColor"
                  />
                  <path
                    d="M92.0309 2.87248C86.0059 19.3265 77.5769 42.6405 71.6149 59.0555H67.2419L72.0939 45.2285C72.0939 45.2285 65.3449 28.3135 63.9609 24.1285H68.4609C68.4609 24.1425 74.1109 40.1455 74.1159 40.1635L79.7999 23.9215C77.9999 19.7325 73.0249 7.00148 71.6249 2.84448H76.1249C76.1249 2.85848 81.9469 18.6005 81.9519 18.6185L87.5959 2.87248H92.0309Z"
                    fill="currentColor"
                  />
                </g>
              </svg>
            </div>
            {/* Form Header */}
            <div className="auth-header">
              <h1 className="auth-title">
                {isSignUpMode
                  ? "Sign Up / Create Account"
                  : "Sign In / Welcome Back"}
              </h1>
              <p className="auth-subtitle">
                {isSignUpMode
                  ? "Join thousands of organizations using Talent Console"
                  : "Sign in to access your talent management dashboard"}
              </p>
            </div>

            {/* Social Login */}
            <div className="auth-social-section">
              {!isSignUpMode && (
                <button
                  className="auth-social-btn auth-google-btn"
                  onClick={loginWithGoogle}
                  disabled={isLoading}
                >
                  <div className="auth-social-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  </div>
                  <span>Continue with Google</span>
                </button>
              )}

              {!isSignUpMode && (
                <button
                  className="auth-social-btn auth-microsoft-btn"
                  onClick={handleMsAlLogin}
                  disabled={isLoading || inProgress !== InteractionStatus.None}
                >
                  <div className="auth-social-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path fill="#F25022" d="M1 1h10v10H1z" />
                      <path fill="#00A4EF" d="M13 1h10v10H13z" />
                      <path fill="#7FBA00" d="M1 13h10v10H1z" />
                      <path fill="#FFB900" d="M13 13h10v10H13z" />
                    </svg>
                  </div>
                  <span>Continue with Microsoft</span>
                </button>
              )}

              {/* Social Signup */}
              {isSignUpMode && (
                <button
                  className="auth-social-btn auth-google-btn"
                  disabled={isLoading}
                  onClick={loginWithGoogle}
                >
                  <div className="auth-social-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                      />
                    </svg>
                  </div>
                  <span>Sign Up with Google</span>
                </button>
              )}

              {isSignUpMode && (
                <button
                  className="auth-social-btn auth-microsoft-btn"
                  disabled={isLoading}
                  onClick={handleMsAlLogin}
                >
                  <div className="auth-social-icon">
                    <svg width="16" height="16" viewBox="0 0 24 24">
                      <path fill="#F25022" d="M1 1h10v10H1z" />
                      <path fill="#00A4EF" d="M13 1h10v10H13z" />
                      <path fill="#7FBA00" d="M1 13h10v10H1z" />
                      <path fill="#FFB900" d="M13 13h10v10H13z" />
                    </svg>
                  </div>
                  <span>Sign Up with Microsoft</span>
                </button>
              )}
            </div>

            <div className="auth-divider">
              <div className="auth-divider-line"></div>
              <span className="auth-divider-text">or</span>
              <div className="auth-divider-line"></div>
            </div>

            {/* Main Form */}
            <form onSubmit={handleSubmit} className="auth-form">
              {isSignUpMode && (
                <div className="auth-field">
                  <InputField
                    label="Full Name"
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required={isSignUpMode}
                    error={errors.name}
                    icon={User}
                  />
                </div>
              )}

              <div className="auth-field">
                <label htmlFor="email" className="auth-label"></label>
                <InputField
                  label="Email Address"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="Enter your email address"
                  autoComplete="username"
                  required
                  error={errors.email}
                  icon={Mail}
                  showPasswordToggle={false}
                />
              </div>

              {isSignUpMode && (
                <>
                  <div className="auth-field">
                    <label htmlFor="organization" className="auth-label">
                      <Building size={14} />
                      <span>Organization*</span>
                    </label>
                    <input
                      type="text"
                      id="organization"
                      name="organization"
                      value={formData.organization}
                      onChange={handleInputChange}
                      placeholder="Enter your organization"
                      required={isSignUpMode}
                      className="auth-input"
                    />
                  </div>
                  <div className="auth-field">
                    <label className="auth-label">
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <circle cx="12" cy="12" r="10" />
                        <line x1="2" y1="12" x2="22" y2="12" />
                        <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                      </svg>
                      Website*
                    </label>
                    <input
                      type="text"
                      id="website"
                      name="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder="Enter company website"
                      required={isSignUpMode}
                      className="auth-input"
                    />
                  </div>
                  <label className="auth-checkbox">
                    <input
                      type="checkbox"
                      required
                      value={formData.checked}
                      onChange={handleCheckboxChange}
                      checked={formData.checked}
                    />
                    <span className="auth-checkmark"></span>
                    <a
                      style={{ fontSize: "12px" }}
                      href="https://docs.google.com/document/d/1RN1r2ZiJ8uj6l-rIYqVCyxz-0GmDk9MY"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Terms & Conditions
                    </a>
                  </label>
                </>
              )}

              {!isSignUpMode && (
                <InputField
                  label="Password"
                  type="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                  error={errors.password}
                  icon={Lock}
                  showPasswordToggle={true} // Enable password toggle
                />
              )}

              {!isSignUpMode && (
                <div className="auth-options">
                  <label className="auth-checkbox">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={handleRememberMeChange}
                    />
                    <span className="auth-checkmark"></span>
                    <span>Remember me</span>
                  </label>
                  <a
                    onClick={handleForgotPasword}
                    href="#"
                    className="auth-forgot"
                  >
                    Forgot Password?
                  </a>
                </div>
              )}

              {isSignUpMode ? (
                <button
                  className="auth-submit"
                  onClick={handleSignUp}
                  disabled={isLoading || !formData.checked} // Prevent multiple clicks
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="spin" size={16} />
                      Signing Up...
                    </>
                  ) : (
                    "Sign Up"
                  )}
                </button>
              ) : (
                <button
                  className="auth-submit"
                  onClick={handleLogin}
                  disabled={
                    isLoading ||
                    formData.password === "" ||
                    formData.email === "" ||
                    formData.password.length < 8
                  } // Prevent multiple clicks
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="spin" size={16} />
                      Signing In...
                    </>
                  ) : (
                    "Sign In"
                  )}
                </button>
              )}
            </form>

            {/* Form Footer */}
            <div className="auth-footer">
              <p className="auth-footer-text">
                {isSignUpMode
                  ? "Already have an account?"
                  : "Don't have an account?"}{" "}
                <button
                  type="button"
                  className="auth-switch"
                  onClick={() => navigate("/signup")}
                  // onClick={() => setIsSignUpMode(!isSignUpMode)}
                >
                  {isSignUpMode ? "Sign In" : "Sign Up"}
                </button>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* **************************************************************************************************** */}
      {/* Auth Type Mismatch Modal */}
      <FeedbackModal
        show={authMismatchModal.show}
        onClose={() =>
          setAuthMismatchModal({
            show: false,
            title: "",
            message: "",
          })
        }
        onConfirm={() =>
          setAuthMismatchModal({
            show: false,
            title: "",
            message: "",
          })
        }
        type="error"
        title={authMismatchModal.title || "Couldn't sign you in"}
        message={authMismatchModal.message}
        showButton={true}
      />

      {/* Forgot Password Modal */}
      <FeedbackModal
        show={isForgotOpen}
        onClose={closeForgot}
        onConfirm={submitForgot}
        type="info"
        title="Reset your password"
        message="Enter the email associated with your account. We'll send you a reset link."
        showButton={false}
      >
        <form onSubmit={submitForgot} style={{ marginTop: "1rem" }}>
          <div className="auth-field">
            <label htmlFor="forgot-email" className="auth-label">
              <Mail size={14} />
              <span>
                Email Address <span style={{ color: "red" }}>*</span>
              </span>
            </label>

            <input
              id="forgot-email"
              type="email"
              className="auth-input"
              placeholder="you@company.com"
              value={forgotEmail}
              onChange={(e) => {
                setForgotEmail(e.target.value);
                setForgotError("");
              }}
              required
              autoFocus
            />

            {forgotError && (
              <p
                className="input-field-error-message"
                style={{ color: "#dc2626", marginTop: 8, fontSize: "12px" }}
              >
                {forgotError}
              </p>
            )}
          </div>

          <div
            className="modal-footer"
            style={{
              display: "flex",
              gap: 8,
              marginTop: 24,
              justifyContent: "flex-end",
            }}
          >
            <button
              type="button"
              className="btn btn-secondary"
              onClick={closeForgot}
              disabled={forgotSending}
              style={{
                border: "1px solid #d0d5dd",
                padding: "10px 20px",
                borderRadius: "8px",
                background: "white",
                color: "#000",
                cursor: "pointer",
                fontSize: "14px",
                fontWeight: "500",
              }}
            >
              Cancel
            </button>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={forgotSending || !isValidEmail(forgotEmail)}
              style={{
                borderRadius: "8px",
                padding: "10px 20px",
                background: "#0e1822",
                color: "white",
                border: "none",
                cursor:
                  forgotSending || !isValidEmail(forgotEmail)
                    ? "not-allowed"
                    : "pointer",
                opacity: forgotSending || !isValidEmail(forgotEmail) ? 0.6 : 1,
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {forgotSending && <Loader2 className="spin" size={16} />}
              {forgotSending ? "Sending..." : "Send reset link"}
            </button>
          </div>
        </form>
      </FeedbackModal>
    </div>
  );
}
